import { useField } from 'formik'
import React, { useEffect, useRef, useState } from 'react'
import { Form } from 'react-bootstrap'
import styled from 'styled-components'
import { ChromePicker } from 'react-color'
import { FormGroup } from 'components'
import { _, colorRegex, colors } from 'utils'
import { useClickOutSide } from 'hooks'
import { TextFieldProps } from '../types'

const S = {
    FormLabel: styled(Form.Label)`
        display: block;
        width: 100%;
        margin: 0;
    `,
    ColorInput: styled.div<{ isDisabled: boolean }>`
        position: relative;
        display: flex;
        align-items: center;
        width: 100%;
        height: 44px;
        padding: 10px 16px;
        border: 1px solid ${colors.inputBorder};
        font-size: 16px;
        line-height: 19px;
        font-weight: 300;
        color: ${colors.dark};
        border-radius: 4px;
        cursor: ${({ isDisabled }) => (isDisabled ? 'auto' : 'pointer')};
        background-color: ${({ isDisabled }) => isDisabled && '#efefef'};
    `,
    ColorIndicator: styled.div<{ color: string }>`
        width: 24px;
        height: 24px;
        margin-right: 8px;
        border-radius: 4px;
        box-shadow: 0 0 1px rgba(0, 0, 0);
        background-color: ${({ color }) => color};
    `,
    HiddenInput: styled(Form.Control)`
        opacity: 0 !important;
        height: 0 !important;
        margin: 0 !important;
        padding: 0 !important;
    `,
    Input: styled.input`
        width: calc(100% - 32px);
        height: 100%;
        border: none;
        outline: none;
        font-size: 14px;
        color: ${colors.grayTitle};
    `,
    ColorPopup: styled.div`
        position: absolute;
        bottom: 0;
        left: 0;
        z-index: 1;
        transform: translateY(100%);
    `
}

const ColorField = ({
    name,
    label,
    helperText,
    isRequired,
    isDisabled,
    isReadOnly,
    defaultValue,
    listenerWrapperId,
    ...props
}: TextFieldProps) => {
    const [
        field,
        meta,
        helper
    ] = useField({ name })
    const { error, touched } = meta
    const [isShow, setIsShow] = useState(false)
    const ref = useRef()
    useClickOutSide(ref, () => isShow && setIsShow(false), listenerWrapperId)

    useEffect(() => {
        if (!field.value && defaultValue) {
            helper.setValue(defaultValue)
        }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [defaultValue])

    return (
        <FormGroup
            controlId={`${field.name}-color-field`}
            {...props}
        >
            <Form.Label>
                {label}
                {isRequired && <span>&nbsp;*</span>}
            </Form.Label>
            <S.FormLabel>
                <S.ColorInput isDisabled={isDisabled}>
                    <S.ColorIndicator
                        onClick={() => setIsShow(!isShow)}
                        color={colorRegex.test(field.value) ? field.value : 'transparent'}
                    />
                    <S.Input
                        value={field.value || '#fff'}
                        onChange={(e) => helper.setValue(e.target.value)}
                        onBlur={() => helper.setTouched(true)}
                    />
                    {isShow && (
                        <S.ColorPopup ref={ref}>
                            <ChromePicker
                                onChangeComplete={(color) => helper.setValue(color.hex)}
                                color={field.value || '#fff'}
                            />
                        </S.ColorPopup>
                    )}
                </S.ColorInput>
            </S.FormLabel>

            {!(touched && error) && <Form.Text muted>{helperText}</Form.Text>}

            {touched && error && (
                <small className="text-danger invalid-field">
                    {_(error)}
                </small>
            )}
        </FormGroup>
    )
}

export default ColorField
